import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Grid, Tooltip } from "@mui/material";
import classnames from "classnames";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { BsTrash } from "react-icons/bs";
import { FaSave } from "react-icons/fa";
import { FiEdit } from "react-icons/fi";
import { MdKeyboardArrowDown, MdKeyboardArrowUp, MdOutlineCancel } from "react-icons/md";

import { MOBILE_MAX_SIZE } from "../../../../constants";
import { AgentServiceBooking } from "../../../../models/agent.service.booking";
import { Currency } from "../../../../models/currency";
import { EstimateGroup, EstimateService } from "../../../../models/estimate";
import { ChargeOptions } from "../../../../models/service";
import groupBy from "../../../../util/groupBy";
import { TotalItemValues } from "../../../../util/inventory";
import { calculateSubtotalByChargeAndByEstimate, formatCurrency } from "../../../../util/prices";
import { Input } from "../../../Form";
import { ServiceSection } from "../ServiceSection";
import { AllowedColumn, IconColor, KeyboardKey } from "../types";
import { GroupHeader } from "./CustomerView/GroupHeader";
import { ServiceSectionMobile } from "./CustomerView/ServiceSection";
import useStyles from "./styles";

interface GroupSectionProps {
  groups?: EstimateGroup[];
  priceIndex: number;
  priceSequence?: number;
  ["data-cy"]?: string;
  isEstimateMode?: boolean;
  allCurrencies: Currency[];
  allowedColumns: AllowedColumn[];
  totalInventoryValues: TotalItemValues;
  onChangeGroups: (priceIndex: number, groups: EstimateGroup[]) => void;
  agentServices?: AgentServiceBooking[];
  isOnViewMode?: boolean;
  onCustomerExpandedGroups?: number[];
  onCustomerView?: boolean;
}

export const GroupSection: React.FC<GroupSectionProps> = (props) => {
  const {
    groups,
    priceIndex,
    priceSequence,
    onChangeGroups,
    isEstimateMode,
    allCurrencies,
    allowedColumns,
    totalInventoryValues,
    agentServices = [],
    isOnViewMode = false,
    onCustomerExpandedGroups = [],
    onCustomerView = false,
  } = props;
  const classes = useStyles();
  const { t } = useTranslation();

  const [groupsExpanded, setGroupsExpanded] = useState<Array<number>>(
    onCustomerExpandedGroups ?? []
  );
  const [groupEditing, setGroupEditing] = useState(-1);
  const [newGroupName, setNewGroupName] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const getDataCy = useCallback(
    (key: string) => `${props["data-cy"]}${props["data-cy"] ? "-" : ""}${key}`,
    [props["data-cy"]]
  );

  const defaultGroupName = useMemo(() => `${t("newGroup")}`, [t]);

  const hasOneGroup = useMemo(() => (groups?.length || 0) <= 1, [groups]);

  const groupIsExpanded = useCallback(
    (groupIndex: number) =>
      groupsExpanded.filter((group) => Number(group) === Number(groupIndex)).length > 0,
    [groupsExpanded]
  );
  const handleExpandGroup = (groupIndex: number) => {
    const expanded = groupIsExpanded(groupIndex);

    if (!expanded) {
      setGroupsExpanded((prev) => [...prev, groupIndex]);
    } else {
      setGroupsExpanded((prev) => prev.filter((old) => old !== groupIndex));
    }
  };

  // TODO Felipe use this function to add new group inside component
  // const onAddNewGroup = useCallback((priceIndex: number) => {
  //     if (onChangePrices) {
  //         let clonePrices = _.cloneDeep((prices || []) as EstimatePrice[]);

  //         const length = clonePrices[priceIndex]?.groups?.length || 0;
  //         clonePrices[priceIndex] = {
  //             ...clonePrices[priceIndex],
  //             groups: [
  //                 ...(clonePrices[priceIndex].groups || []),
  //                 {
  //                     name: defaultGroupName,
  //                     estimateServices: getEstimateServiceArrayDefault(currentUser?.companyId, currentUser?.company?.currency)
  //                 }
  //             ]
  //         };

  //         onChangePrices(clonePrices);
  //         setPriceGroupEditing([priceIndex, length]);
  //         setNewGroupName(defaultGroupName);
  //         setPriceGroupExpanded(prev => [...prev, [priceIndex, length]]);
  //         setTimeout(() => {
  //             inputRef.current?.focus();
  //         }, 100);
  //         NotificationToast.success(t("groupAddedSuccessfully"), { id: "new-group-success" });
  //     };
  // }, [prices, inputRef]);

  const onDeleteGroup = (groupIndex: number) => {
    const cloneGroups = _.cloneDeep(groups || []) as EstimateGroup[];

    if (cloneGroups[groupIndex]) {
      cloneGroups.splice(groupIndex, 1);
      onChangeGroups(priceIndex, cloneGroups);
    }
  };

  const onChangeGroupName = (groupIndex: number) => {
    const cloneGroups = _.cloneDeep(groups || []) as EstimateGroup[];

    if (cloneGroups && cloneGroups[groupIndex]) {
      cloneGroups[groupIndex].name = newGroupName || defaultGroupName;
      onChangeGroups(priceIndex, cloneGroups);
      setGroupEditing(-1);
      setNewGroupName("");
    }
  };

  const onCancelChangeGroupName = useCallback(() => {
    setGroupEditing(-1);
    setNewGroupName("");
  }, []);

  const onOpenInputToChangeGroupName = (groupIndex: number) => {
    const group = groups?.[groupIndex];

    if (group) {
      setGroupEditing(groupIndex);
      setNewGroupName(group.name || "");
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  };

  const getTotalByGroupAndCurrencySymbol = (groupIndex: number) => {
    const group = groups?.[groupIndex];

    if (group) {
      const estimates = group.estimateServices || [];
      const currencyTotal: { [x: string]: number } = {};

      const grouppedByCurrency: { [x: string]: EstimateService[] } = groupBy(
        estimates,
        "currencyId"
      );

      if (grouppedByCurrency && Object.keys(grouppedByCurrency).length > 0) {
        Object.keys(grouppedByCurrency).forEach((currencyId) => {
          const currency = allCurrencies.find(
            (currency) => String(currency.id) === String(currencyId)
          );

          if (currency && currency.code) {
            const estimatesGroupped = grouppedByCurrency[currencyId] || [];
            const total = estimatesGroupped?.reduce((prev, curr) => {
              const subTotal = calculateSubtotalByChargeAndByEstimate(
                curr.charge as keyof ChargeOptions,
                curr.rate,
                curr.quantity,
                curr.discount,
                curr.margin,
                curr.markUp,
                totalInventoryValues.volumeConverted
              );

              return prev + Number(subTotal || 0);
            }, 0);

            currencyTotal[currency.code] = total;
          }
        });
      }

      return currencyTotal;
    }

    return null;
  };

  const renderTotalCalculatedByGroup = (groupIndex: number) => {
    const totalByCurrencySymbol = getTotalByGroupAndCurrencySymbol(groupIndex);

    if (
      totalByCurrencySymbol &&
      totalByCurrencySymbol instanceof Object &&
      Object.keys(totalByCurrencySymbol).length > 0
    ) {
      const keys = Object.keys(totalByCurrencySymbol);
      const values = Object.values(totalByCurrencySymbol);

      return (
        <Grid item className={classnames(classes.groupTotalValues, classes.flexRow)}>
          <Grid item className={classes.flexColumn}>
            {keys.map((key) => (
              <span key={key} className={classnames(classes.groupTotalValue, classes.symbol)}>
                {key}
              </span>
            ))}
          </Grid>
          <Grid item className={classes.flexColumnAlignEnd}>
            {values.map((value, index) => {
              const currency = allCurrencies.find((currency) => currency.code === keys[index]);

              return (
                <span key={value} className={classnames(classes.groupTotalValue)}>
                  {formatCurrency(value, currency)}
                </span>
              );
            })}
          </Grid>
        </Grid>
      );
    }

    return (
      <Grid item className={classes.groupTotalValues}>
        <Grid container item direction="row" wrap="nowrap">
          <Grid item>
            {/* // TODO Felipe this line need use translated word */}
            <span className={classes.groupTotalValue}>$0.00</span>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const onChangeEstimateServices = (groupIndex: number, estimateServices: EstimateService[]) => {
    const cloneGroups = _.cloneDeep(groups || []) as EstimateGroup[];

    if (cloneGroups && cloneGroups[groupIndex]) {
      cloneGroups[groupIndex].estimateServices = estimateServices;
      onChangeGroups(priceIndex, cloneGroups);
    }
  };

  useEffect(() => {
    if (onCustomerExpandedGroups?.length) setGroupsExpanded(onCustomerExpandedGroups);
  }, [onCustomerExpandedGroups]);

  return (
    <Fragment>
      {groups?.map((group, groupIndex) => {
        const expanded = groupIsExpanded(groupIndex);
        const groupIsEditig = groupEditing === groupIndex;

        return (
          <Grid
            key={group.id || groupIndex}
            data-cy={getDataCy(`prices-${priceSequence}-group-${group.name}`)}
            container
            direction="column"
            className={classnames(
              classes.groupContainer,
              onCustomerView && classes.groupContainerOnCustomerView
            )}
          >
            {onCustomerView && window.innerWidth <= MOBILE_MAX_SIZE ? (
              <GroupHeader
                groupName={group?.name}
                isGroupExpanded={expanded}
                onExpandGroup={() => handleExpandGroup(groupIndex)}
              />
            ) : (
              <Grid
                data-cy={getDataCy(`prices-${priceSequence}-group-${group.name}-section`)}
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                className={classes.groupHeader}
                wrap="nowrap"
              >
                <Grid
                  item
                  container
                  data-cy={getDataCy(`prices-${priceSequence}-group-${group.name}-section`)}
                  direction="row"
                  justifyContent="space-between"
                  className={classes.groupNameActions}
                >
                  <Grid item>
                    {!!groupIsEditig && (
                      <Input
                        disabled={isOnViewMode}
                        name={`group-${group.name}-input`}
                        value={newGroupName}
                        onChange={(event) => setNewGroupName(event.target.value)}
                        placeholder={t("fillNameAndPressSaveOrEnter")}
                        className={classes.groupNameInput}
                        inputRef={inputRef}
                        onKeyDown={(event) => {
                          if (event.key === KeyboardKey.ENTER) {
                            onChangeGroupName(groupIndex);
                          }
                          if (event.key === KeyboardKey.ESCAPE) {
                            onCancelChangeGroupName();
                          }
                        }}
                      />
                    )}
                    {!groupIsEditig && (
                      <span
                        data-cy={getDataCy(`prices-${priceSequence}-group-${group.name}-name`)}
                        className={classes.groupName}
                      >
                        {group.name}
                      </span>
                    )}
                  </Grid>
                  <Grid item wrap="nowrap" className={classes.flex}>
                    {!!groupIsEditig && !isOnViewMode && (
                      <>
                        <Tooltip title={t("pressToConfirmGroupNameChange")} placement="top">
                          <div
                            data-cy={getDataCy(
                              `prices-${priceSequence}-group-${group.name}-edit-save`
                            )}
                          >
                            <FaSave
                              size={21}
                              color="#308CDB"
                              className={classnames(classes.button, classes.iconMargin)}
                              onClick={() => onChangeGroupName(groupIndex)}
                            />
                          </div>
                        </Tooltip>
                        <Tooltip title={t("pressToCancelGroupNameChange")} placement="top">
                          <div
                            data-cy={getDataCy(
                              `prices-${priceSequence}-group-${group.name}-edit-cancel`
                            )}
                          >
                            <MdOutlineCancel
                              size={21}
                              color="#D92D20"
                              className={classnames(classes.button, classes.iconMargin)}
                              onClick={() => onCancelChangeGroupName()}
                            />
                          </div>
                        </Tooltip>
                      </>
                    )}
                    {!groupIsEditig && !isOnViewMode && (
                      <>
                        <Tooltip title={t("editGroupName")} placement="top">
                          <div
                            data-cy={getDataCy(`prices-${priceSequence}-group-${group.name}-edit`)}
                          >
                            <FiEdit
                              size={20}
                              color="#465869"
                              className={classes.button}
                              onClick={() => onOpenInputToChangeGroupName(groupIndex)}
                            />
                          </div>
                        </Tooltip>
                        <Tooltip
                          title={t(
                            `${
                              hasOneGroup ? "thereMustBeAtLeastOneGroupInAPrice" : "deleteThisGroup"
                            }`
                          )}
                          placement="top"
                        >
                          <div
                            data-cy={getDataCy(
                              `prices-${priceSequence}-group-${group.name}-delete`
                            )}
                          >
                            <BsTrash
                              size={20}
                              color={hasOneGroup ? IconColor.DISABLED : IconColor.DANGER}
                              className={classnames(
                                !hasOneGroup && classes.button,
                                classes.iconMargin
                              )}
                              onClick={() => {
                                if (!hasOneGroup) onDeleteGroup(groupIndex);
                              }}
                            />
                          </div>
                        </Tooltip>
                      </>
                    )}
                    {!!isEstimateMode && <div className={classes.verticalSeparator}></div>}
                  </Grid>
                </Grid>

                <Grid
                  data-cy={getDataCy(`prices-${priceSequence}-group-${group.name}-total`)}
                  item
                  container
                  direction="row"
                  alignItems="baseline"
                  justifyContent="flex-end"
                  wrap="nowrap"
                  className={classnames(
                    classes.groupTotalExpand,
                    !isEstimateMode && classes.groupTotalExpandNotShowed
                  )}
                >
                  {!!isEstimateMode && (
                    <Grid
                      data-cy={getDataCy(`prices-${priceSequence}-group-${group.name}-total-label`)}
                      item
                      className={classes.groupTotal}
                    >
                      <span className={classes.groupTotalLabel}>{t("total")}</span>
                    </Grid>
                  )}
                  <Grid
                    data-cy={getDataCy(
                      `prices-${priceSequence}-group-${group.name}-expand_collapse`
                    )}
                    item
                    wrap="nowrap"
                    alignItems="center"
                    className={classes.flex}
                  >
                    {!!isEstimateMode && renderTotalCalculatedByGroup(groupIndex)}
                    {expanded ? (
                      <div onClick={() => handleExpandGroup(groupIndex)}>
                        <MdKeyboardArrowUp
                          size={24}
                          color="#465869"
                          className={classnames(classes.button, classes.iconArrow)}
                        />
                      </div>
                    ) : (
                      <div onClick={() => handleExpandGroup(groupIndex)}>
                        <MdKeyboardArrowDown
                          size={24}
                          color="#465869"
                          className={classnames(classes.button, classes.iconArrow)}
                        />
                      </div>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            )}

            {expanded && (
              <>
                {onCustomerView && window.innerWidth <= MOBILE_MAX_SIZE ? (
                  <ServiceSectionMobile
                    group={group}
                    isEstimateMode={isEstimateMode}
                    allCurrencies={allCurrencies}
                    totalInventoryVolume={totalInventoryValues}
                  />
                ) : (
                  <ServiceSection
                    isOnViewMode={isOnViewMode}
                    allCurrencies={allCurrencies}
                    allowedColumns={allowedColumns}
                    groupIndex={groupIndex}
                    onChangeEstimateServices={onChangeEstimateServices}
                    totalInventoryValues={totalInventoryValues}
                    group={group}
                    isEstimateMode={isEstimateMode}
                    estimateServices={group.estimateServices}
                    agentServices={agentServices}
                    onCustomerView={onCustomerView}
                  />
                )}
              </>
            )}
          </Grid>
        );
      })}
    </Fragment>
  );
};
